<template>
  <div class="flex items-center mr-8 md:w-1/2 mt-4 mb-8">
    <select
      v-model="form.analyticsServiceYears"
      class="flex-1 ml-2 border border-gray-300 outline-none rounded-md py-2 px-3 bg-white placeholder-gray-400 text-gray-500 appearance-none w-full block focus:outline-none">
      <option
        v-for="(serviceYear, i) in SCHOOL_RESULT_SERVICE_YEARS"
        :key="i"
        :value="i">
        {{ serviceYear }}
      </option>
    </select>
    <button
      class="col-span-2 relative ml-8 px-6 py-2 rounded bg-blue-600 text-blue-50 max-w-max shadow-sm hover:shadow-lg"
      @click.prevent="onFilterButtonClicked">
      絞り込む
    </button>
  </div>
  <template v-if="!schoolExamineesCount">
    <div
      class="mx-auto w-11/12 bg-red-100 border border-red-400 text-red-500 px-4 py-3 mt-8 rounded relative text-center"
      role="alert">
      <span class="block sm:inline font-bold">学生の解答状況が見つかりませんでした。</span>
    </div>
  </template>
  <template v-else>
    <div class="md:flex">
      <div class="md:w-1/2">
        <div class="ml-4">
          <p class="mb-6">校内受験者分析 最終集計日時：{{ getSchoolScoreLastUpdate() }}</p>
          <h1 class="text-lg font-bold mb-2">校内受験者数</h1>
          <p class="ml-2 mb-6">{{ schoolExamineesCount }}名</p>
          <h1 class="text-lg font-bold mb-2">校内：合計点の最低・平均点・最高点</h1>
          <ul class="ml-2 mb-8">
            <li>最低点: {{ schoolTotalLowScore }}/200</li>
            <li>平均点: {{ SchoolTotalAverageScore }}/200</li>
            <li>最高点: {{ schoolTotalHighestScore }}/200</li>
          </ul>
        </div>
        <h1 class="ml-4 mt-6 text-lg font-bold">校内：種別ごとの最低点・平均点・最高点</h1>
        <BarChart
          ref="barRef"
          class=" mt-2"
          :chart-data="SchoolExaminationPercentageData"
          :options="barOptions" />
      </div>
      <div class="md:w-1/2 ml-8">
        <div class="ml-4">
          <p class="mb-6">全国受験者分析 最終集計日時：{{ getScoreLastUpdate() }}</p>
          <h1 class="text-lg font-bold mb-2">全国受験者数</h1>
          <p class="ml-2 mb-6">{{ examineesCount }}名</p>
          <h1 class="text-lg font-bold mb-2">全国：合計点の最低点・平均点・最高点</h1>
          <ul class="ml-2 mb-8">
            <li>最低点: {{ TotalLowScore }}/200</li>
            <li>平均点: {{ TotalAverageScore }}/200</li>
            <li>最高点: {{ TotalHighestScore }}/200</li>
          </ul>
        </div>
        <h1 class="ml-4 mt-6 text-lg font-bold">全国：種別ごとの最低点・平均点・最高点</h1>
        <BarChart
          ref="barRef"
          class="mt-2"
          :chart-data="ExaminationPercentageData"
          :options="barOptions" />
      </div>
    </div>
    <BarChart
      ref="barRef"
      class="mt-12 md:mx-12"
      :chart-data="SubjectCorrentAnswerRateData"
      :options="barOptions" />
    <div class="shadow-lg bg-white mr-1 rounded-b-lg rounded-t px-4 py-5 mt-12 max-h-96 overflow-y-auto">
      <data-table
        hover-disabled
        drag-disabled
        :headers="headers"
        :items="schoolQuestionCorrentAnswerRate">
        <template #exam_type="{ item }">
          <div class="text-left px-6">
            {{ ExaminationType(item.examination_type) }}
          </div>
        </template>
        <template #index="{ item }">
          <div class="text-center px-6">
            {{ item.sort_index + 1 }}
          </div>
        </template>
        <template #subject="{ item }">
          <div class="text-lest px-6">
            {{ subjectName(item.subject_id) }}
          </div>
        </template>
        <template #school_answer_rate="{ item }">
          <div class="text-center px-6">
            {{ item.correct_answer_rate }}%
          </div>
        </template>
        <template #answer_rate="{ item }">
          <div class="text-center px-6">
            {{ getCorrectAnswerRate(item.question_id) }}%
          </div>
        </template>
        <template #question="{ item }">
          <div class="text-left px-6">
            {{ getQuestion(item.question_id) }}
          </div>
        </template>
      </data-table>
    </div>
  </template>
</template>

<script setup>
import {
  defineProps,
  ref,
  computed,
  reactive,
} from 'vue'
import { Chart, registerables } from 'chart.js'
import moment from 'moment'
import { Inertia } from '@inertiajs/inertia'
import { BarChart } from 'vue-chart-3'
import formatDate from '@/actions/format/date'
import DataTable from '@/Common/DataTable'
import AppClientSchoolLayout from '@/Layouts/AppClientSchoolLayout'
import { SCHOOL_RESULT_SERVICE_YEARS } from '@/Constants/serviceYears'

const props = defineProps({
  users: {
    type: Object,
    default: () => ({}),
  },
  questions: {
    type: Array,
    default: () => [],
  },
  examinations: {
    type: Array,
    default: () => [],
  },
  examinationTypes: {
    type: Object,
    default: () => ({}),
  },
  subjectNames: {
    type: Array,
    default: () => [],
  },
  selectedExamination: {
    type: Object,
    default: () => ({}),
  },
  examinationScore: {
    type: Array,
    default: () => [],
  },
  questionCorrentAnswerRate: {
    type: Array,
    default: () => [],
  },
  subjectCorrentAnswerRate: {
    type: Array,
    default: () => [],
  },
  examineesCount: {
    type: Number,
    default: 0,
  },
  schoolExaminationScore: {
    type: Array,
    default: () => [],
  },
  schoolSubjectCorrentAnswerRate: {
    type: Array,
    default: () => [],
  },
  schoolQuestionCorrentAnswerRate: {
    type: Array,
    default: () => [],
  },
  schoolExamineesCount: {
    type: Number,
    default: 0,
  },
  schoolTotalLowScore: {
    type: Number,
    default: 0,
  },
  schoolTotalHighestScore: {
    type: Number,
    default: 0,
  },
  totalLowScore: {
    type: Number,
    default: 0,
  },
  totalHighestScore: {
    type: Number,
    default: 0,
  },
  analyticsServiceYears: {
    type: Number,
    default: 0,
  },
  showTabDisplay: {
    type: Number,
    default: 0,
  },
})

Chart.register(...registerables)

const headers = {
  exam_type: '種別',
  index: '問題番号',
  subject: '科目',
  school_answer_rate: '校内正答率',
  answer_rate: '全国正答率',
  question: '問題文',
}

const form = reactive(Inertia.form({
  analyticsServiceYears: props.analyticsServiceYears,
}))

const ExaminationType = (examinationType) => props.examinationTypes[examinationType]
const subjectName = (subjectId) => props.subjectNames.find(item => item.id === subjectId).name

const barRef = ref()
const barOptions = ref({
  responsive: true,
  plugins: {
    legend: {
      labels: {
        font: {
          size: 16,
        },
      },
    },
  },
})

const TotalLowScore = computed(() => {
  const totalLowScore = props.examinationScore[0]?.lowest_score + props.examinationScore[1]?.lowest_score + props.examinationScore[2]?.lowest_score

  return totalLowScore || 0
})

const TotalAverageScore = computed(() => {
  const totalAverageScore = props.examinationScore[0]?.average_score + props.examinationScore[1]?.average_score + props.examinationScore[2]?.average_score

  return totalAverageScore || 0
})

const TotalHighestScore = computed(() => {
  const totalHighestScore = props.examinationScore[0]?.highest_score + props.examinationScore[1]?.highest_score + props.examinationScore[2]?.highest_score

  return totalHighestScore || 0
})

const SchoolTotalAverageScore = computed(() => {
  const totalAverageScore = props.schoolExaminationScore[0]?.average_score + props.schoolExaminationScore[1]?.average_score + props.schoolExaminationScore[2]?.average_score

  return totalAverageScore || 0
})

const ExaminationPercentageData = computed(() => {
  const chartData = {
    labels: ['必須', '一般', '実地'],
    datasets: [
      {
        label: '最低点(%)',
        data: [
          props.examinationScore[0]?.lowest_score * 2,
          props.examinationScore[1]?.lowest_score,
          props.examinationScore[2]?.lowest_score * 2,
        ],
        backgroundColor: '#C0392B',
      },
      {
        label: '平均点(%)',
        data: [
          props.examinationScore[0]?.average_score * 2,
          props.examinationScore[1]?.average_score,
          props.examinationScore[2]?.average_score * 2,
        ],
        backgroundColor: '#F5B041',
      },
      {
        label: '最高点(%)',
        data: [
          props.examinationScore[0]?.highest_score * 2,
          props.examinationScore[1]?.highest_score,
          props.examinationScore[2]?.highest_score * 2,
        ],
        backgroundColor: '#2ECC71',
      },
    ],
  }
  return chartData
})

const SchoolExaminationPercentageData = computed(() => {
  const chartData = {
    labels: ['必須', '一般', '実地'],
    datasets: [
      {
        label: '最低点(%)',
        data: [
          props.schoolExaminationScore[0]?.lowest_score * 2,
          props.schoolExaminationScore[1]?.lowest_score,
          props.schoolExaminationScore[2]?.lowest_score * 2,
        ],
        backgroundColor: '#C0392B',
      },
      {
        label: '平均点(%)',
        data: [
          props.schoolExaminationScore[0]?.average_score * 2,
          props.schoolExaminationScore[1]?.average_score,
          props.schoolExaminationScore[2]?.average_score * 2,
        ],
        backgroundColor: '#F5B041',
      },
      {
        label: '最高点(%)',
        data: [
          props.schoolExaminationScore[0]?.highest_score * 2,
          props.schoolExaminationScore[1]?.highest_score,
          props.schoolExaminationScore[2]?.highest_score * 2,
        ],
        backgroundColor: '#2ECC71',
      },
    ],
  }
  return chartData
})

const SubjectCorrentAnswerRateData = computed(() => {
  const chartData = {
    labels: ['基礎動物学', '基礎動物看護学', '臨床動物看護学', '愛護・適正飼養学', '実習'],
    datasets: [
      {
        label: '校内：科目ごとの正答率(%)',
        data: [
          props.schoolSubjectCorrentAnswerRate[0]?.correct_answer_rate,
          props.schoolSubjectCorrentAnswerRate[1]?.correct_answer_rate,
          props.schoolSubjectCorrentAnswerRate[2]?.correct_answer_rate,
          props.schoolSubjectCorrentAnswerRate[3]?.correct_answer_rate,
          props.schoolSubjectCorrentAnswerRate[4]?.correct_answer_rate,
        ],
        backgroundColor: '#2ECC71',
      }, {
        label: '全国：科目ごとの正答率(%)',
        data: [
          props.subjectCorrentAnswerRate[0]?.correct_answer_rate,
          props.subjectCorrentAnswerRate[1]?.correct_answer_rate,
          props.subjectCorrentAnswerRate[2]?.correct_answer_rate,
          props.subjectCorrentAnswerRate[3]?.correct_answer_rate,
          props.subjectCorrentAnswerRate[4]?.correct_answer_rate,
        ],
        backgroundColor: '#3BFF8C',
      },
    ],
  }
  return chartData
})

const getCorrectAnswerRate = (questionId) => {
  const question = props.questionCorrentAnswerRate.find(value => value.question_id === questionId)

  if (question) {
    return question.correct_answer_rate
  }
  return '-'
}

const getQuestion = (questionId) => {
  const question = props.questions.find((value) => value.question_id === questionId)

  if (question) {
    return question.question
  }
  return '-'
}

const getSchoolScoreLastUpdate = () => {
  if (props.schoolExaminationScore.length) {
    return formatDate(props.schoolExaminationScore[0].updated_at)
  }
  return '未集計'
}

const getScoreLastUpdate = () => {
  if (props.examinationScore.length) {
    return formatDate(props.examinationScore[0].updated_at)
  }
  return '未集計'
}

const onFilterButtonClicked = () => {
  Inertia.get(route('school.result', {
    examinationId: props.selectedExamination.id,
    analyticsServiceYears: form.analyticsServiceYears,
    showTabDisplay: 1,
  }))
}
</script>
